import { Injectable } from '@angular/core';
import { AlertController, LoadingController, Platform } from '@ionic/angular';

@Injectable({
  providedIn: 'root'
})
export class AlertService {
  constructor(
    public alertController: AlertController, 
    public loadingController: LoadingController,
    private platform: Platform
  ) {}

  private getButtonText(): string {
    return this.platform.is('ios') ? 'Aceptar' : '';
  }

  async presentLoading() {
    const loading = await this.loadingController.create({
      message: 'Espera por favor...',
      translucent: true
    })

    await loading.present()
  }

  async dismiss() {
    setTimeout(() => {
      this.loadingController
        .dismiss()
        .then((res) => {})
        .catch((error) => {})
    }, 700)
  }

  async statusSuccessRegisterRequestSample() {
    const alert = await this.alertController.create({
      header: 'Registro Exitoso!',
      cssClass: 'alert-request',
      mode: 'md',
      subHeader:
        'Nos pondremos en contacto con el paciente para gestionar la Toma de Muestra. Podrá hacer seguimiento de esta solicitud desde el "Historial de Solicitudes"',
      message: 'Este registro cuenta como Orden Médica; no es necesario que genere una orden en físico.',
      buttons: [
        {
          text: this.getButtonText(),
          role: 'cancel',
          cssClass: 'icon-close',
          handler: (data) => {}
        }
      ]
    })

    await alert.present()
  }

  async statusSuccessRegisterRequestGather() {
    const alert = await this.alertController.create({
      header: 'Registro Exitoso!',
      cssClass: 'alert-request',
      mode: 'md',
      subHeader:
        'Solicitud de Retiro de Muestra Enviada. Podrá hacer seguimiento de esta solicitud desde el "Historial de Solicitudes"',
      message: 'Por favor enviar la muestra con Orden Médica y Consentimiento Informado.',
      buttons: [
        {
          text: this.getButtonText(),
          role: 'cancel',
          cssClass: 'icon-close',
          handler: (data) => {}
        }
      ]
    })

    await alert.present()
  }

  async disclaimerPersonalData() {
    return new Promise<boolean>(async (resolve) => {
      const alert = await this.alertController.create({
        header: '',
        subHeader: 'Estimado Dr(a).',
        message: 'Le informamos que el aviso de Manejo de Datos Personales ha cambiado. Le solicitamos amablemente que lo lea y lo acepte para iniciar sesión en GoDx App.',
        buttons: [
          {
            text: 'Rechazar',
            handler: () => {
              resolve(false);
            }
          },
          {
            text: 'Aceptar',
            handler: () => {
              resolve(true);
            }
          },
        ]
      });

      await alert.present();
    });
  }

  async disclaimerValidatePersonalData() {
    return new Promise<boolean>(async (resolve) => {
      const alert = await this.alertController.create({
        header: '',
        subHeader: 'Estimado Dr(a).',
        message: 'La aceptación de los nuevos términos es obligatoria para hacer uso de la App, si no acepta los términos se cerrará su sesión, ¿desea rechazar los nuevos términos?',
        buttons: [
          {
            text: 'Rechazar',
            handler: () => {
              resolve(false);
            }
          },
          {
            text: 'Cancelar',
            handler: () => {
              resolve(true);
            }
          },
        ]
      });

      await alert.present();
    });
  }

  async statusSuccessRegisterRequestKits() {
    const alert = await this.alertController.create({
      header: 'Registro Exitoso!',
      cssClass: 'alert-request-kits',
      mode: 'md',
      subHeader:
        'El tiempo estimado para la entrega de kits de diagnóstico es de hasta 6 días hábiles. Recuerde que puede hacer seguimiento a su solicitud a través del modulo "Historial de solicitudes - Solicitud de Kits"',
      buttons: [
        {
          text: this.getButtonText(),
          role: 'cancel',
          cssClass: 'icon-close',
          handler: (data) => {}
        }
      ]
    })

    await alert.present()
  }

  async errorFields() {
    const alert = await this.alertController.create({
      header: '',
      cssClass: 'alert-error-fields',
      mode: 'md',
      message: 'Faltan campos por llenar',
      buttons: [
        {
          text: this.getButtonText(),
          role: 'cancel',
          cssClass: 'icon-close2',
          handler: (data) => {}
        }
      ]
    })
    await alert.present()
  }

  async statusSuccessRegister(option: string) {
    const alert = await this.alertController.create({
      header: 'Registro con éxito!',
      mode: 'md',
      cssClass: 'alert-purple',
      subHeader: 'Se ha registrado ' + option + ' con éxito',
      buttons: [
        {
          text: this.getButtonText(),
          role: 'cancel',
          cssClass: 'icon-close',
          handler: (data) => {}
        }
      ]
    })

    await alert.present()
  }

  async statusErrorRegister(option: string) {
    const alert = await this.alertController.create({
      header: 'Registro Fallido!',
      cssClass: 'alert-white',
      mode: 'md',
      subHeader: 'No se ha registrado ' + option + ' con éxito, por favor vuelva a intentar',
      buttons: [
        {
          text: this.getButtonText(),
          role: 'cancel',
          cssClass: 'icon-close2',
          handler: (data) => {}
        }
      ]
    })

    await alert.present()
  }

  async statusErrorUpdate(option: string) {
    const alert = await this.alertController.create({
      header: 'Actualización Fallida!',
      cssClass: 'alert-white',
      mode: 'md',
      subHeader: 'No se ha actualizado ' + option + ' con éxito, por favor vuelva a intentar',
      buttons: [
        {
          text: this.getButtonText(),
          role: 'cancel',
          cssClass: 'icon-close2',
          handler: (data) => {}
        }
      ]
    })

    await alert.present()
  }

  async statusAlert(header: string, subHeader: string, onClose?: () => void) {
    const alert = await this.alertController.create({
      header: header,
      cssClass: 'alert-white',
      subHeader: subHeader,
      buttons: [{
        text: this.getButtonText(),
        role: 'cancel',
        cssClass: 'icon-close2',
        handler: (data) => {}
      }],
      backdropDismiss: true
    })

    await alert.present()
    
    await alert.onDidDismiss()
    if (onClose) {
      onClose()
    }
  }

  async statusSuccessUpdate(option: string) {
    const alert = await this.alertController.create({
      header: 'Actualización con éxito!',
      cssClass: 'alert-purple',
      mode: 'md',
      subHeader: 'Se ha actualizado ' + option + ' con éxito',
      buttons: [
        {
          text: this.getButtonText(),
          role: 'cancel',
          cssClass: 'icon-close',
          handler: (data) => {}
        }
      ]
    })

    await alert.present()
  }

  async validateGender() {
    const alert = await this.alertController.create({
      header: 'Cambio de Género!',
      cssClass: 'alert-white',
      mode: 'md',
      subHeader:
        'Disculpe, hemos detectado que se cambió el género del paciente indicado en el formulario. Se recomienda validar esta información en el formulario.',
      buttons: [
        {
          text: this.getButtonText(),
          role: 'cancel',
          cssClass: 'icon-close2',
          handler: (data) => {}
        }
      ]
    })

    await alert.present()
  }
}
